import React, { useEffect, useState, useRef, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";

import { socket } from "../../socket";

import { alpha, useTheme } from "@mui/material/styles";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Paper,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DesktopDatePicker as DatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
  getTimelineByProductId,
  updateTimelineTask,
  getPresupuestoInfoByProductId,
} from "../../api/Estimates/estimatesApi";
import { getUserById } from "../../api/User/userApi";
import {
  PERMISO_EXPECTED_CP,
  canEditExpectedDate,
  collectSessionUnlockedExpectedIds,
  hasPendingExpectedToLock,
  isSameDateKey,
  mergePendingExpectedDates,
  normalizePickerDate,
  pendingExpectedForTask,
} from "./expectedDateCpLock";

import NotasDialog from "./NotasDialog";
import NotesIcon from "@mui/icons-material/Notes";
import LogisticsSection from "./LogisticsSection";
import DocumentosLogistica from "../Logistica/DocumentosLogistica";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import { COLLAPSIBLE_GROUPS as CP_HEADER_GROUPS } from "./CollapsibleHeaderGroup";
import {
  applyGridSnapshotToTimeline,
  mergeMissingHeaderGroupTasks,
  sameTaskName,
  sortTimelineTasksByFieldOrder,
} from "./criticalPathTimelineUtils";
import { buildRecalculatedCriticalDatePayloads } from "./criticalPathCriticalDates";

dayjs.locale("es");

const GROUP_COLOR_MAP = {
  "Samples and approvals": "#fff9c4a3",
  Production: "#90b2b74d",
  "Critical dates": "#d4ff305c",
  Logistic: "#ffac7e3d",
};

/** Mismos campos que el DataGrid horizontal (evita desalineación con V_CriticalPathHorizontal / timeline). */
function headerGroupFields(groupId) {
  const fields = CP_HEADER_GROUPS[groupId];
  if (!Array.isArray(fields)) return [];
  return fields.filter((f) => !String(f).startsWith("__placeholder"));
}

const COLLAPSIBLE_GROUPS = {
  "Samples and approvals": headerGroupFields("Muestras y aprobaciones"),
  Production: headerGroupFields("Producción"),
  "Critical dates": headerGroupFields("Fechas críticas"),
  Logistic: headerGroupFields("Envío y logística"),
};

const CriticalPathDrawer = ({
  open,
  onClose,
  onDirty,
  mode = "PRODUCCION", // 👈 IMPORTANTE
  productId = null,
  idPresupuesto = null,
  idPresupuestoA3Pedido = null,
  idMuestra = null,
  legacyLogistics = [],
  gridRowSnapshot = null,
  muestraHeaderInfo = null,
  /** Solo filas que ya tenían ProductionLogistics (no envíos nuevos): edición legacy en el drawer. */
  hasLegacyLogisticsRow = false,
  /** Si false, no se monta `LogisticsSection` (evita `logisticsApi` / fabricas cuando la logística se gestiona fuera). */
  embedLogisticsInDrawer = true,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [timeline, setTimeline] = useState([]);
  const [presupuestoInfo, setPresupuestoInfo] = useState(null);
  const lastLoadedRef = useRef(null);
  const loadingRef = useRef(false);
  const hasChangesRef = useRef(false);
  const socketRefreshTimerRef = useRef(null);

  const isDark = theme.palette.mode === "dark";

  const [notasOpen, setNotasOpen] = useState(false);
  const [canEditLockedExpected, setCanEditLockedExpected] = useState(false);
  const [expectedPermLoaded, setExpectedPermLoaded] = useState(false);
  const [lockHintDismissed, setLockHintDismissed] = useState(false);
  const [expectedError, setExpectedError] = useState("");
  const [sessionUnlockedExpectedIds, setSessionUnlockedExpectedIds] = useState(
    () => new Set(),
  );
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const pendingExpectedByIdRef = useRef({});
  const loadedExpectedByIdRef = useRef({});
  const sessionExpectedInitRef = useRef(false);
  const timelineRef = useRef([]);
  const dirtyDatesRef = useRef({});
  timelineRef.current = timeline;

  const isMuestra = mode === "MUESTRA";

  const timelinePresupuestoOpts = useMemo(() => {
    if (
      idPresupuestoA3Pedido != null &&
      Number.isFinite(Number(idPresupuestoA3Pedido))
    ) {
      return { idPresupuestoA3Pedido: Number(idPresupuestoA3Pedido) };
    }
    if (idPresupuesto != null && Number.isFinite(Number(idPresupuesto))) {
      return { idPresupuesto: Number(idPresupuesto) };
    }
    return {};
  }, [idPresupuesto, idPresupuestoA3Pedido]);

  const timelineDisplayOpts = useMemo(() => {
    const idPed =
      idPresupuestoA3Pedido != null &&
      Number.isFinite(Number(idPresupuestoA3Pedido))
        ? Number(idPresupuestoA3Pedido)
        : null;
    const hasPedidoCp = idPed != null;
    return {
      // Fila legacy sin pedido A3 → mostrar timeline legacy (fechas del grid / API).
      preferLegacyTimeline: Boolean(hasLegacyLogisticsRow && !hasPedidoCp),
      // Fila con pedido A3 → solo timeline del pedido (no mezclar con legacy).
      hideLegacyTimeline: Boolean(hasLegacyLogisticsRow && hasPedidoCp),
      idPresupuestoA3Pedido: idPed,
      // Logística (Nº bultos, ETA, etc.) → solo LogisticsSection, no tarjetas CP.
      excludeLogisticFields: true,
    };
  }, [hasLegacyLogisticsRow, idPresupuestoA3Pedido]);

  const applyDirtyDates = (rows) => {
    const dirty = dirtyDatesRef.current;
    if (!dirty || Object.keys(dirty).length === 0) return rows;
    return rows.map((t) => {
      let next = t;
      for (const field of ["ExpectedDate", "UpdatedDate", "RealDate"]) {
        const key = `${t.Id}:${field}`;
        if (!Object.prototype.hasOwnProperty.call(dirty, key)) continue;
        if (isSameDateKey(t[field], dirty[key])) {
          delete dirty[key];
          continue;
        }
        if (next === t) next = { ...t };
        next[field] = dirty[key];
      }
      return next;
    });
  };

  const applyTimelineRows = (rows, { fromInitialLoad = false } = {}) => {
    const list = Array.isArray(rows) ? rows : [];
    if (fromInitialLoad && !sessionExpectedInitRef.current) {
      sessionExpectedInitRef.current = true;
      setSessionUnlockedExpectedIds(collectSessionUnlockedExpectedIds(list));
      loadedExpectedByIdRef.current = Object.fromEntries(
        list.map((t) => [t.Id, t.ExpectedDate ?? null]),
      );
    }
    const next = applyDirtyDates(
      mergePendingExpectedDates(
        mergeMissingHeaderGroupTasks(list),
        pendingExpectedByIdRef.current,
      ),
    );
    timelineRef.current = next;
    setTimeline(next);
  };

  const commitTimeline = (updater) => {
    const current = timelineRef.current;
    const next = typeof updater === "function" ? updater(current) : updater;
    timelineRef.current = next;
    setTimeline(next);
    return next;
  };

  const markDirtyDates = (taskUpdates) => {
    for (const { id, field, value } of taskUpdates) {
      dirtyDatesRef.current[`${id}:${field}`] = value;
    }
  };

  const recordPendingExpected = (updates) => {
    pendingExpectedByIdRef.current = {
      ...pendingExpectedByIdRef.current,
      ...updates,
    };
  };

  const putMuestraTimeline = async (body) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/api/productionTimeline/timeline`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(body),
      },
    );
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      const error = new Error(errBody.error || "Error al guardar fecha");
      error.status = res.status;
      throw error;
    }
  };

  useEffect(() => {
    if (!open) return;
    setLockHintDismissed(false);
    setExpectedError("");
    setExpectedPermLoaded(false);
    setCloseConfirmOpen(false);
    setSessionUnlockedExpectedIds(new Set());
    pendingExpectedByIdRef.current = {};
    loadedExpectedByIdRef.current = {};
    sessionExpectedInitRef.current = false;
    dirtyDatesRef.current = {};

    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const userId = token ? jwtDecode(token)?.userId : null;
        if (!userId) {
          if (!cancelled) {
            setCanEditLockedExpected(false);
            setExpectedPermLoaded(true);
          }
          return;
        }
        const user = await getUserById(userId);
        if (!cancelled) {
          setCanEditLockedExpected(Boolean(user?.[PERMISO_EXPECTED_CP]));
        }
      } catch (err) {
        console.error("Error cargando Permisos_Expected_CP:", err);
        if (!cancelled) setCanEditLockedExpected(false);
      } finally {
        if (!cancelled) setExpectedPermLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    // 🟣 MUESTRAS
    if (isMuestra && idMuestra) {
      loadingRef.current = true;
      setPresupuestoInfo(muestraHeaderInfo || null);

      (async () => {
        try {
          const token = localStorage.getItem("token");

          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/productionTimeline/timeline-muestra/${idMuestra}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          const data = await response.json();

          // 🔧 Normalizamos SOLO lo necesario
          const normalized = data.map((t) => ({
            ...t,
            Id: t.Id, // 👈 este endpoint YA devuelve el Id correcto
            ExpectedDate: t.ExpectedDate || null,
            UpdatedDate: t.UpdatedDate || null,
            RealDate: t.RealDate || null,
          }));

          applyTimelineRows(normalized, { fromInitialLoad: true });
          lastLoadedRef.current = `MUESTRA_${idMuestra}`;
        } catch (err) {
          console.error("❌ Error cargando timeline muestra:", err);
        } finally {
          loadingRef.current = false;
        }
      })();

      return;
    }

    // 🟢 PRODUCCIÓN (varias líneas pueden compartir IdProducto → idPresupuesto en la query).
    if (!isMuestra && productId) {
      let cancelled = false;
      loadingRef.current = true;

      (async () => {
        try {
          const [data, info] = await Promise.all([
            getTimelineByProductId(productId, timelinePresupuestoOpts),
            getPresupuestoInfoByProductId(productId, timelinePresupuestoOpts),
          ]);

          if (cancelled) return;

          applyTimelineRows(
            applyGridSnapshotToTimeline(
              data,
              gridRowSnapshot,
              isMuestra,
              timelineDisplayOpts,
            ),
            { fromInitialLoad: true },
          );
          setPresupuestoInfo(info);
        } catch (err) {
          console.error("❌ Error cargando timeline producto:", err);
        } finally {
          if (!cancelled) loadingRef.current = false;
        }
      })();

      return () => {
        cancelled = true;
        loadingRef.current = false;
      };
    }
  }, [
    open,
    isMuestra,
    productId,
    idMuestra,
    idPresupuesto,
    muestraHeaderInfo,
    timelinePresupuestoOpts,
    gridRowSnapshot,
    timelineDisplayOpts,
  ]);

  // ✅ Escucha los cambios en tiempo real de otros usuarios
  // ✅ Escucha actualizaciones en tiempo real
  useEffect(() => {
    if (!open || !productId || isMuestra) return;

    const refreshFromApi = async () => {
      try {
        const [dataTimeline, dataInfo] = await Promise.all([
          getTimelineByProductId(productId, timelinePresupuestoOpts),
          getPresupuestoInfoByProductId(productId, timelinePresupuestoOpts),
        ]);
        // Tras guardar: solo API (gridRowSnapshot es estático al abrir el drawer y pisaba fechas nuevas).
        applyTimelineRows(
          applyGridSnapshotToTimeline(
            Array.isArray(dataTimeline) ? dataTimeline : [],
            null,
            isMuestra,
            timelineDisplayOpts,
          ),
        );
        setPresupuestoInfo(dataInfo);
      } catch (err) {
        console.error("❌ Error al refrescar tras socket:", err);
      }
    };

    const handleTimelineUpdate = (data) => {
      if (data?.productId !== productId) return;
      if (
        idPresupuestoA3Pedido != null &&
        Number.isFinite(Number(idPresupuestoA3Pedido)) &&
        data?.idPresupuestoA3Pedido != null &&
        Number(data.idPresupuestoA3Pedido) !== Number(idPresupuestoA3Pedido)
      ) {
        return;
      }
      if (hasLegacyLogisticsRow && data?.legacyPurged) return;

      if (socketRefreshTimerRef.current) {
        clearTimeout(socketRefreshTimerRef.current);
      }
      socketRefreshTimerRef.current = setTimeout(() => {
        socketRefreshTimerRef.current = null;
        refreshFromApi();
      }, 200);
    };

    socket.on("timelineUpdated", handleTimelineUpdate);

    return () => {
      socket.off("timelineUpdated", handleTimelineUpdate);
      if (socketRefreshTimerRef.current) {
        clearTimeout(socketRefreshTimerRef.current);
        socketRefreshTimerRef.current = null;
      }
    };
  }, [
    open,
    productId,
    isMuestra,
    timelinePresupuestoOpts,
    timelineDisplayOpts,
    hasLegacyLogisticsRow,
    idPresupuestoA3Pedido,
  ]);

  const handleChange = async (taskId, field, value) => {
    try {
      if (taskId == null || !Number.isFinite(Number(taskId))) return;
      const formattedValue = normalizePickerDate(value);
      if (formattedValue === undefined) return;

      const task = timelineRef.current.find((t) => t.Id === taskId);
      if (!task) return;

      if (isSameDateKey(task[field], formattedValue)) return;

      const expectedDate =
        field === "ExpectedDate" ? formattedValue : task.ExpectedDate;
      const updatedDate =
        field === "UpdatedDate" ? formattedValue : task.UpdatedDate;
      const realDate = field === "RealDate" ? formattedValue : task.RealDate;

      const expectedForApi =
        pendingExpectedForTask(pendingExpectedByIdRef.current, task.Id) ===
        undefined
          ? expectedDate
          : (loadedExpectedByIdRef.current[task.Id] ??
            loadedExpectedByIdRef.current[String(task.Id)] ??
            null);

      const localPayload = {
        ExpectedDate: expectedDate,
        UpdatedDate: updatedDate,
        RealDate: realDate,
      };
      const apiPayload = {
        ExpectedDate: expectedForApi,
        UpdatedDate: updatedDate,
        RealDate: realDate,
      };

      const sessionUnlocked = sessionUnlockedExpectedIds.has(task.Id);
      if (
        field === "ExpectedDate" &&
        !canEditExpectedDate(canEditLockedExpected, task.ExpectedDate, {
          sessionUnlocked,
        })
      ) {
        setExpectedError(t("CriticalPathHorizontal.expectedLockedError"));
        return;
      }

      const recalculatedPayloads = buildRecalculatedCriticalDatePayloads(
        timelineRef.current,
        task.TaskName,
        field,
        formattedValue,
        {
          allowOverwriteExpected: canEditLockedExpected,
          unlockedExpectedIds: sessionUnlockedExpectedIds,
        },
      );

      const applyOptimistic = () => {
        if (recalculatedPayloads.length > 0) {
          commitTimeline((prev) =>
            prev.map((t) => {
              const recalculated = recalculatedPayloads.find(
                ({ task: recalculatedTask }) => recalculatedTask.Id === t.Id,
              );
              return recalculated ? { ...t, ...recalculated.payload } : t;
            }),
          );
          markDirtyDates(
            recalculatedPayloads.map(
              ({ task: recalculatedTask, payload: p }) => ({
                id: recalculatedTask.Id,
                field,
                value: p[field],
              }),
            ),
          );
        } else {
          commitTimeline((prev) =>
            prev.map((t) => (t.Id === taskId ? { ...t, ...localPayload } : t)),
          );
          markDirtyDates([{ id: taskId, field, value: formattedValue }]);
        }
      };

      if (
        field === "ExpectedDate" &&
        !canEditLockedExpected &&
        sessionUnlocked
      ) {
        const updates = {};
        if (recalculatedPayloads.length > 0) {
          recalculatedPayloads.forEach(
            ({ task: recalculatedTask, payload: p }) => {
              updates[recalculatedTask.Id] = p.ExpectedDate;
            },
          );
        } else {
          updates[taskId] = formattedValue;
        }
        applyOptimistic();
        recordPendingExpected(updates);
        hasChangesRef.current = true;
        return;
      }

      applyOptimistic();

      // 🟣 MUESTRAS → NO optimistic update
      if (isMuestra) {
        if (recalculatedPayloads.length > 0) {
          await Promise.all(
            recalculatedPayloads.map(({ task: recalculatedTask, payload: p }) =>
              putMuestraTimeline({
                ExpectedDate: p.ExpectedDate,
                UpdatedDate: p.UpdatedDate,
                RealDate: p.RealDate,
                ID_MUESTRA: idMuestra,
                TaskName: recalculatedTask.TaskName,
              }),
            ),
          );
        } else {
          await putMuestraTimeline({
            ...apiPayload,
            ID_MUESTRA: idMuestra,
            TaskName: task.TaskName,
          });
        }

        // 🔄 RELEER timeline real desde backend
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/productionTimeline/timeline-muestra/${idMuestra}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const fresh = await res.json();
        applyTimelineRows(fresh);

        hasChangesRef.current = true;
        return; // ⛔ NO sigas
      }

      // 🟢 PRODUCCIÓN
      if (recalculatedPayloads.length > 0) {
        await Promise.all(
          recalculatedPayloads.map(({ task: recalculatedTask, payload: p }) =>
            updateTimelineTask(recalculatedTask.Id, {
              ExpectedDate: p.ExpectedDate,
              UpdatedDate: p.UpdatedDate,
              RealDate: p.RealDate,
            }),
          ),
        );
      } else {
        await updateTimelineTask(taskId, apiPayload);
      }

      hasChangesRef.current = true;
    } catch (error) {
      console.error("❌ Error al guardar fecha:", error);
      if (error?.status === 403 || error?.response?.status === 403) {
        setExpectedError(
          error?.response?.data?.error ||
            error?.message ||
            t("CriticalPathHorizontal.expectedLockedError"),
        );
      }
    }
  };

  const isEmptyOrLate = (date) => !date || dayjs(date).isBefore(dayjs(), "day");

  const finishClose = () => {
    loadingRef.current = false;
    setCloseConfirmOpen(false);
    onClose({
      changed: hasChangesRef.current,
      ProductId: productId,
      idMuestra,
      timeline,
    });
    hasChangesRef.current = false;
  };

  const persistPendingExpectedAndClose = async () => {
    try {
      const pending = pendingExpectedByIdRef.current;
      const toSave = Object.entries(pending).filter(([, date]) =>
        Boolean(date),
      );
      if (toSave.length > 0) {
        if (isMuestra) {
          await Promise.all(
            toSave.map(([id, expected]) => {
              const current = timeline.find(
                (row) => String(row.Id) === String(id),
              );
              if (!current) return null;
              return putMuestraTimeline({
                ExpectedDate: expected,
                UpdatedDate: current.UpdatedDate,
                RealDate: current.RealDate,
                ID_MUESTRA: idMuestra,
                TaskName: current.TaskName,
              });
            }),
          );
        } else {
          await Promise.all(
            toSave.map(([id, expected]) => {
              const current = timeline.find(
                (row) => String(row.Id) === String(id),
              );
              if (!current) return null;
              return updateTimelineTask(current.Id, {
                ExpectedDate: expected,
                UpdatedDate: current.UpdatedDate,
                RealDate: current.RealDate,
              });
            }),
          );
        }
        hasChangesRef.current = true;
      }
      pendingExpectedByIdRef.current = {};
      finishClose();
    } catch (error) {
      console.error("❌ Error al guardar Expected al cerrar:", error);
      setExpectedError(
        error?.response?.data?.error ||
          error?.message ||
          t("CriticalPathHorizontal.expectedLockedError"),
      );
    }
  };

  const handleClose = () => {
    if (
      !canEditLockedExpected &&
      hasPendingExpectedToLock(pendingExpectedByIdRef.current)
    ) {
      setCloseConfirmOpen(true);
      return;
    }
    finishClose();
  };

  const hasSessionUnlockedExpected = sessionUnlockedExpectedIds.size > 0;

  const renderTaskField = (task) => {
    const isExpectedLate = isEmptyOrLate(task.ExpectedDate) && !task.RealDate;
    const isUpdatedLate = isEmptyOrLate(task.UpdatedDate) && !task.RealDate;
    const isRealLate =
      task.RealDate &&
      task.ExpectedDate &&
      dayjs(task.RealDate).isAfter(dayjs(task.ExpectedDate));
    const expectedLocked = !canEditExpectedDate(
      canEditLockedExpected,
      task.ExpectedDate,
      { sessionUnlocked: sessionUnlockedExpectedIds.has(task.Id) },
    );

    return (
      <Box
        mt={1}
        sx={{
          display: "flex",
          flexDirection: "column",
          border: "1px solid",
          borderColor:
            isExpectedLate || isUpdatedLate || isRealLate
              ? alpha(theme.palette.error.main, 0.32)
              : "divider",
          borderRadius: 2,
          p: 1.25,
          minWidth: 160,
          backgroundColor: isDark
            ? alpha(theme.palette.background.paper, 0.78)
            : alpha(theme.palette.background.paper, 0.94),
          boxShadow: `0 6px 16px ${alpha(
            theme.palette.common.black,
            isDark ? 0.35 : 0.07,
          )}`,
        }}
      >
        <Typography
          variant="body2"
          fontWeight={800}
          mb={1}
          sx={{
            color: "text.primary",
            textAlign: "center",
            lineHeight: 1.2,
            minHeight: 34,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title={task.TaskName === "DD" ? "Delivery Date" : undefined}
        >
          {task.TaskName}
        </Typography>

        <DatePicker
          label="Expected"
          format="DD/MM/YYYY"
          disabled={expectedLocked}
          value={task.ExpectedDate ? dayjs(task.ExpectedDate) : null}
          onChange={(date) => {
            if (expectedLocked) return;
            handleChange(task.Id, "ExpectedDate", date);
          }}
          slotProps={{
            textField: {
              size: "small",
              sx: {
                width: "100%",
                maxWidth: 150,
                "& .MuiInputBase-root": {
                  backgroundColor: isExpectedLate
                    ? isDark
                      ? alpha(theme.palette.error.main, 0.32)
                      : alpha(theme.palette.error.main, 0.16)
                    : theme.palette.caja?.main ||
                      theme.palette.background.default,
                  borderRadius: 1.3,
                },
              },
            },
            actionBar: {
              actions: expectedLocked ? [] : ["clear"],
            },
          }}
          clearable={!expectedLocked}
        />

        <DatePicker
          label="Updated"
          format="DD/MM/YYYY"
          value={task.UpdatedDate ? dayjs(task.UpdatedDate) : null}
          onChange={(date) => handleChange(task.Id, "UpdatedDate", date)}
          slotProps={{
            textField: {
              size: "small",
              sx: {
                width: "100%",
                maxWidth: 150,
                mt: 1.5,
                /*  bgcolor: isUpdatedLate ? "#ffcccc" : undefined, */
                "& .MuiInputBase-root": {
                  backgroundColor: isUpdatedLate
                    ? isDark
                      ? alpha(theme.palette.error.main, 0.32)
                      : alpha(theme.palette.error.main, 0.16)
                    : theme.palette.caja?.main ||
                      theme.palette.background.default,
                  borderRadius: 1.3,
                },
              },
            },
            actionBar: {
              actions: ["clear"],
            },
          }}
          clearable
        />

        <DatePicker
          label="Real"
          format="DD/MM/YYYY"
          value={task.RealDate ? dayjs(task.RealDate) : null}
          onChange={(date) => handleChange(task.Id, "RealDate", date)}
          slotProps={{
            textField: {
              size: "small",
              sx: {
                width: "100%",
                maxWidth: 150,
                mt: 1.5,
                /*  bgcolor: isRealLate ? "#ffe6b3" : undefined, */
                "& .MuiInputBase-root": {
                  backgroundColor: isRealLate
                    ? isDark
                      ? alpha(theme.palette.warning.main, 0.32)
                      : alpha(theme.palette.warning.main, 0.18)
                    : theme.palette.caja?.main ||
                      theme.palette.background.default,
                  borderRadius: 1.3,
                },
              },
            },
            actionBar: {
              actions: ["clear"],
            },
          }}
          clearable
        />
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Drawer
        anchor="right"
        open={open}
        onClose={null}
        PaperProps={{
          sx: {
            width: { xs: "100%", lg: "92vw", xl: 1550 },
            maxWidth: "100vw",
            borderLeft: "1px solid",
            borderColor: "divider",
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.background.default
                : "#f7f8fb",
          },
        }}
      >
        <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              mb: 2,
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              background: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.main,
                0.12,
              )} 0%, ${alpha(theme.palette.info.main, 0.08)} 100%)`,
              backdropFilter: "blur(10px)",
              boxShadow: `0 8px 24px ${alpha(
                theme.palette.common.black,
                isDark ? 0.35 : 0.1,
              )}`,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                minWidth={0}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "primary.main",
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                    backgroundColor: alpha(theme.palette.primary.main, 0.14),
                    flexShrink: 0,
                  }}
                >
                  <RouteOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    sx={{ opacity: 0.85, letterSpacing: 1, lineHeight: 1 }}
                  >
                    {isMuestra
                      ? "Sample Critical Path"
                      : "Production Critical Path"}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.15,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {presupuestoInfo?.DiseñoMuestra ||
                      presupuestoInfo?.designCliente ||
                      (isMuestra
                        ? `Muestra #${idMuestra}`
                        : `Producto #${productId}`)}
                  </Typography>
                  {presupuestoInfo ? (
                    <Stack
                      direction="row"
                      spacing={0.75}
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mt: 0.75 }}
                    >
                      {presupuestoInfo.designCliente ? (
                        <Chip
                          size="small"
                          label={presupuestoInfo.designCliente}
                          sx={{
                            fontWeight: 700,
                            color: "primary.main",
                            borderColor: alpha(
                              theme.palette.primary.main,
                              0.35,
                            ),
                          }}
                          variant="outlined"
                        />
                      ) : null}
                      {presupuestoInfo.NombrePresupuesto ? (
                        <Chip
                          size="small"
                          label={presupuestoInfo.NombrePresupuesto}
                          sx={{ fontWeight: 700 }}
                          variant="outlined"
                        />
                      ) : null}
                      {presupuestoInfo.FechaAceptacion ? (
                        <Chip
                          size="small"
                          label={dayjs(presupuestoInfo.FechaAceptacion).format(
                            "DD/MM/YYYY",
                          )}
                          sx={{
                            fontWeight: 700,
                            color: "#FF8A50",
                            borderColor: alpha("#FF8A50", 0.45),
                          }}
                          variant="outlined"
                        />
                      ) : null}
                    </Stack>
                  ) : null}
                </Box>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                justifyContent="flex-end"
              >
                {/* 📝 BOTÓN NOTAS */}
                <Tooltip title="Open Notes">
                  <IconButton
                    onClick={() => setNotasOpen(true)}
                    aria-label="Open Notes"
                    sx={{
                      color: "info.main",
                      border: "1px solid",
                      borderColor: "info.main",
                      height: 34,
                      width: 34,
                      backgroundColor: alpha(theme.palette.info.main, 0.08),
                      "&:hover": {
                        backgroundColor: "info.main",
                        color: "#fff",
                      },
                    }}
                  >
                    <NotesIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* ❌ CERRAR DRAWER */}
                <IconButton
                  onClick={handleClose}
                  aria-label="Close drawer"
                  sx={{
                    color: "error.main",
                    border: "1px solid",
                    borderColor: "error.main",
                    height: 34,
                    width: 34,
                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                    "&:hover": { backgroundColor: "error.main", color: "#fff" },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Paper>

          {expectedPermLoaded ? (
            <Box
              data-testid="cp-expected-perm"
              data-can-edit={canEditLockedExpected ? "1" : "0"}
              sx={{ display: "none" }}
            />
          ) : null}

          {expectedPermLoaded &&
          !canEditLockedExpected &&
          !lockHintDismissed ? (
            <Alert
              severity="warning"
              onClose={() => setLockHintDismissed(true)}
              sx={{ mb: 2 }}
            >
              {hasSessionUnlockedExpected
                ? t("CriticalPathHorizontal.expectedLockHint")
                : t("CriticalPathHorizontal.expectedLockedAllHint")}
            </Alert>
          ) : null}

          {Object.entries(COLLAPSIBLE_GROUPS).map(([group, tasks]) => {
            const allGroupTasks = timeline.filter((t) =>
              tasks.some((label) => sameTaskName(t.TaskName, label)),
            );
            const legacyTimelineTaskId =
              allGroupTasks.find((t) =>
                sameTaskName(t.TaskName, "Shipment Method"),
              )?.Id ||
              allGroupTasks[0]?.Id ||
              null;
            const isLogisticGroup = group === "Logistic";
            const groupTasks = isLogisticGroup
              ? []
              : sortTimelineTasksByFieldOrder(allGroupTasks, tasks);
            const timelineCardTasks = groupTasks;
            const hasTimelineCards = timelineCardTasks.length > 0;

            if (!hasTimelineCards && group !== "Logistic") return null;
            if (isLogisticGroup && (!embedLogisticsInDrawer || isMuestra)) {
              return null;
            }

            return (
              <Paper
                key={group}
                elevation={0}
                sx={{
                  mt: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  overflow: "hidden",
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                <Box
                  sx={{
                    backgroundColor:
                      GROUP_COLOR_MAP[group] ||
                      alpha(theme.palette.primary.main, 0.08),
                    px: 2,
                    py: 1.15,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={900}
                      sx={{ lineHeight: 1.2 }}
                    >
                      {group}
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        group === "Logistic"
                          ? "Logistics"
                          : `${timelineCardTasks.length} tasks`
                      }
                      sx={{
                        height: 22,
                        fontWeight: 700,
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          0.7,
                        ),
                      }}
                    />
                  </Stack>
                </Box>

                {hasTimelineCards ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 1,
                      p: 1.25,
                      overflowX: "auto",
                      scrollbarWidth: "thin",
                    }}
                  >
                    {timelineCardTasks.map((task) => (
                      <Box
                        key={task.Id ?? `missing:${task.TaskName}`}
                        sx={{
                          flexShrink: 0,
                        }}
                      >
                        {renderTaskField(task)}
                      </Box>
                    ))}
                  </Box>
                ) : null}

                {group === "Logistic" &&
                  !isMuestra &&
                  embedLogisticsInDrawer && (
                    <Box
                      sx={{
                        p: 1.5,
                        borderColor: "divider",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      {productId ? (
                        <DocumentosLogistica
                          productId={productId}
                          idPresupuesto={idPresupuesto}
                          embedded
                        />
                      ) : null}
                      {hasLegacyLogisticsRow ? (
                        <LogisticsSection
                          productId={productId}
                          idPresupuesto={idPresupuesto}
                          idPresupuestoA3Pedido={idPresupuestoA3Pedido}
                          numeroPresupuesto={
                            presupuestoInfo?.NumeroPresupuesto ?? null
                          }
                          timelineTaskId={legacyTimelineTaskId}
                          legacyLogistics={legacyLogistics}
                          onDirty={onDirty}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Esta línea no tiene fila ProductionLogistics editable
                          aquí. Los envíos nuevos se gestionan en «Gestión
                          logística de pedidos». Los documentos PDF/BL subidos
                          desde logística aparecen arriba en «Documentos de
                          logística».
                        </Typography>
                      )}
                    </Box>
                  )}
              </Paper>
            );
          })}
        </Box>
      </Drawer>
      <Dialog
        open={closeConfirmOpen}
        onClose={() => setCloseConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("CriticalPathHorizontal.expectedLockOnCloseTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("CriticalPathHorizontal.expectedLockOnCloseBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCloseConfirmOpen(false)}>
            {t("CriticalPathHorizontal.expectedLockOnCloseStay")}
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={persistPendingExpectedAndClose}
          >
            {t("CriticalPathHorizontal.expectedLockOnCloseConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(expectedError)}
        autoHideDuration={5000}
        onClose={() => setExpectedError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="warning"
          onClose={() => setExpectedError("")}
          sx={{ width: "100%" }}
        >
          {expectedError}
        </Alert>
      </Snackbar>
      <NotasDialog
        open={notasOpen}
        productId={productId}
        idMuestra={idMuestra}
        idPresupuestoA3Pedido={idPresupuestoA3Pedido}
        onClose={() => {
          setNotasOpen(false);
        }}
      />
    </LocalizationProvider>
  );
};

export default CriticalPathDrawer;
